import { type ReactNode } from 'react';
import { hasPermission, type UserRole, type Permissions } from '../utils/abac';

export interface WithPermissionProps<Resource extends keyof Permissions> {
  roles: UserRole[];
  resource: Resource;
  action: Permissions[Resource]['action'];
  data?: Permissions[Resource]['dataType'];
  children: ReactNode;
  fallback?: ReactNode;
}

export function WithPermission<Resource extends keyof Permissions>({
  roles,
  resource,
  action,
  data,
  children,
  fallback = null,
}: WithPermissionProps<Resource>) {
  const permitted = hasPermission(roles, resource, action, data);

  return permitted ? children : fallback;
}

export function withPermission<P extends { roles: UserRole[] }, R extends keyof Permissions>(
  Component: React.ComponentType<P>,
  getPermissionProps: (props: P) => {
    resource: R;
    action: Permissions[R]['action'];
    data?: Permissions[R]['dataType'];
  },
) {
  return function (props: P) {
    const permissionProps = getPermissionProps(props);
    const permitted = hasPermission(
      props.roles,
      permissionProps.resource,
      permissionProps.action,
      permissionProps.data,
    );

    if (!permitted) {
      return null;
    }

    return <Component {...props} />;
  };
}
